import { useState, useEffect } from 'react'
import { FiRefreshCw, FiCreditCard, FiChevronLeft } from 'react-icons/fi'
import toast from 'react-hot-toast'
import SeatMap from '../../components/SeatMap'
import SectorMap from '../../components/SectorMap'
import {
    adminGetEvents,
    adminCashierIssueTicket,
    getEventPerformances,
    getPerformanceSeats,
    getPerformanceTicketTypes,
} from '../../services/api'
import useCartStore from '../../stores/cartStore'

export default function CashierDesk() {
    const [events, setEvents] = useState([])
    const [selectedEvent, setSelectedEvent] = useState(null)
    const [performances, setPerformances] = useState([])
    const [selectedPerfId, setSelectedPerfId] = useState('')

    // Performance context
    const [performanceData, setPerformanceData] = useState(null)
    const [seats, setSeats] = useState([])
    const [sectors, setSectors] = useState([])
    const [selectedSector, setSelectedSector] = useState(null)
    const [soldSeatIds, setSoldSeatIds] = useState([])
    const [lockedSeatIds, setLockedSeatIds] = useState([])
    const [ticketTypes, setTicketTypes] = useState([])

    // Cashier selection state from store
    const {
        selectedSeats,
        ticketAssignments,
        assignTicketType,
        clearCart
    } = useCartStore()

    const [customerName, setCustomerName] = useState('')

    const [loading, setLoading] = useState(true)
    const [processing, setProcessing] = useState(false)

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [selectedSector])

    useEffect(() => {
        loadEvents()

        // Clean store on mount/unmount
        clearCart()
        return () => clearCart()
    }, [])

    // Auto assignment of default tickets when seats are selected
    useEffect(() => {
        selectedSeats.forEach(seat => {
            if (!ticketAssignments[seat.id] && ticketTypes.length > 0) {
                assignTicketType(seat.id, ticketTypes[0].id)
            }
        })
    }, [selectedSeats, ticketTypes, ticketAssignments, assignTicketType])

    const loadEvents = async () => {
        try {
            const res = await adminGetEvents({ page: 1, per_page: 1000, active: true })
            setEvents(res.data.data || res.data || [])
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const handleEventChange = async (eventId) => {
        const ev = events.find(e => e.id === parseInt(eventId))
        setSelectedEvent(ev)
        setSelectedPerfId('')
        setPerformanceData(null)
        setSectors([])
        setSelectedSector(null)
        clearCart()

        if (!ev) {
            setPerformances([])
            return
        }

        try {
            const res = await getEventPerformances(ev.slug)
            setPerformances(res.data.data || res.data || [])
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        }
    }

    const handleLoadPerformance = async (perfId) => {
        if (!perfId) return
        setSelectedPerfId(perfId)
        setLoading(true)
        clearCart()

        try {
            const perf = performances.find(p => p.id === parseInt(perfId))
            setPerformanceData(perf)

            const [seatsRes, typesRes] = await Promise.all([
                getPerformanceSeats(perfId),
                getPerformanceTicketTypes(perfId)
            ])

            setSeats(seatsRes.data.seats || [])
            const loadedSectors = seatsRes.data.sectors || []
            setSectors(loadedSectors)
            setSoldSeatIds(seatsRes.data.sold_seat_ids || [])
            setLockedSeatIds(seatsRes.data.locked_seat_ids || [])
            setTicketTypes(typesRes.data || [])

            // Keep cashier flow aligned with checkout behavior.
            if (loadedSectors.length === 1) {
                setSelectedSector(loadedSectors[0])
            } else {
                setSelectedSector(null)
            }

        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const calculateSubtotal = () => {
        if (!performanceData) return 0
        return selectedSeats.reduce((total, seat) => {
            const ticketTypeId = ticketAssignments[seat.id]?.ticketTypeId || ticketAssignments[seat.id]
            const tz = ticketTypes.find(t => t.id === ticketTypeId)
            if (!tz) return total + parseFloat(performanceData.base_price)

            let price = parseFloat(performanceData.base_price)
            if (tz.is_percentage) {
                price = price * (1 + parseFloat(tz.price_modifier) / 100)
            } else {
                price = price + parseFloat(tz.price_modifier)
            }
            return total + Math.max(0, price)
        }, 0)
    }

    const handleSectorSelect = (sector) => {
        // Avoid hidden stale selections when switching sectors.
        // clearCart()
        setSelectedSector(sector)
    }

    const visibleSeats = selectedSector
        ? seats.filter(s => s.sector_id === selectedSector.id)
        : (sectors.length > 1 ? [] : seats)

    const getSectorName = (sector) => {
        const lang = localStorage.getItem('app_lang') || 'bg'
        return lang === 'en' ? (sector.name_en || sector.name) : sector.name
    }

    const handleIssueTicket = async () => {
        if (selectedSeats.length === 0) {
            return toast.error(window.t('molya_izberete_mesta'))
        }

        const hasAllAssigned = selectedSeats.every(seat => ticketAssignments[seat.id])
        if (!hasAllAssigned) {
            return toast.error(window.t('molya_izberete_tip_bilet_za_vsichki_mesta'))
        }

        setProcessing(true)

        try {
            const tickets = selectedSeats.map(seat => ({
                seat_id: seat.id,
                ticket_type_id: ticketAssignments[seat.id]?.ticketTypeId || ticketAssignments[seat.id],
            }))

            await adminCashierIssueTicket({
                performance_id: parseInt(selectedPerfId),
                tickets,
                customer_name: customerName || 'Каса Клиент'
            })

            toast.success(window.t('biletite_sa_izdadeni_uspeshno'))

            // Reload the performance seats to refresh the map
            clearCart()
            setCustomerName('')
            await handleLoadPerformance(selectedPerfId)

        } catch (error) {
            toast.error(error.response?.data?.message || window.t('greshka_pri_izdavane'))
        } finally {
            setProcessing(false)
        }
    }

    if (loading && !events.length) {
        return <div className="text-gray-400">{window.t('zarezhdane')}</div>
    }

    function getSeatSector(seatSectorId) {
        const seatSector = sectors.find(sector => sector.id === seatSectorId)
        return seatSector ? seatSector.name : ''
    }

    return (
        <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <FiCreditCard className="text-primary-500" />
                {window.t('tsentralna_kasa')}
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">

                {/* Configuration Sidebar */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="card p-4">
                        <label className="label">{window.t('1_izberete_sabitie')}</label>
                        <select
                            className="input w-full"
                            onChange={(e) => handleEventChange(e.target.value)}
                            value={selectedEvent?.id || ''}
                        >
                            <option value="">{window.t('sabitie_1')}</option>
                            {events.map(ev => (
                                <option key={ev.id} value={ev.id}>{ev.title}</option>
                            ))}
                        </select>
                    </div>

                    {selectedEvent && (
                        <div className="card p-4">
                            <label className="label">{window.t('2_izberete_prozhektsiya')}</label>
                            {performances.length === 0 ? (
                                <div className="text-gray-400 text-sm">{window.t('nyama_otkriti_prozhektsii')}</div>
                            ) : (
                                <select
                                    className="input w-full"
                                    onChange={(e) => handleLoadPerformance(e.target.value)}
                                    value={selectedPerfId}
                                >
                                    <option value="">{window.t('prozhektsiya_data')}</option>
                                    {performances.map(perf => (
                                        <option key={perf.id} value={perf.id}>
                                            {new Date(perf.start_time).toLocaleString('bg-BG')}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {selectedSeats.length > 0 && (
                        <div className="card p-4 border border-primary-500/30">
                            <h3 className="text-white font-bold mb-4">{window.t('detayli_za_izdavane')}</h3>

                            <div className="space-y-3 mb-4 max-h-[40vh] md:max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                                {selectedSeats.map(seat => (
                                    <div key={seat.id} className="bg-gray-800 p-2 rounded text-sm">
                                        <div className="flex justify-between text-white font-medium mb-1">
                                            {sectors.length > 1
                                                ? <span>{getSeatSector(seat.sector_id)} - {seat.row} / {seat.number}</span>
                                                : <span>{seat.row} / {seat.number}</span>
                                            }
                                        </div>
                                        <select
                                            className="input py-1 text-xs w-full bg-gray-900 border-gray-700"
                                            value={ticketAssignments[seat.id]?.ticketTypeId || ticketAssignments[seat.id] || ''}
                                            onChange={(e) => assignTicketType(seat.id, parseInt(e.target.value))}
                                        >
                                            {ticketTypes.map(t => (
                                                <option key={t.id} value={t.id}>{localStorage.getItem('app_lang') === 'en' ? (t.name_en || t.name) : t.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>

                            <div className="mb-4">
                                <label className="text-xs text-gray-400">{window.t('ime_na_klient_optsionalno')}</label>
                                <input
                                    type="text"
                                    className="input py-1.5 text-sm w-full"
                                    placeholder={window.t('ivan_ivanov')}
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                />
                            </div>

                            <div className="border-t border-gray-700 pt-3 mb-4">
                                <div className="flex justify-between text-lg font-bold text-white">
                                    <span>{window.t('obshto_za_plashtane')}</span>
                                    <span className="text-primary-400">€{calculateSubtotal().toFixed(2)}</span>
                                </div>
                            </div>

                            <button
                                onClick={handleIssueTicket}
                                disabled={processing}
                                className="btn-primary w-full py-2"
                            >
                                {processing ? `${window.t('izdavane')}` : `${window.t('izday_bileti')} (${window.t('v_broi')})`}
                            </button>
                        </div>
                    )}
                </div>

                {/* Main Content Area (Seat Map) */}
                <div className="md:col-span-2 lg:col-span-3">
                    {loading && selectedPerfId ? (
                        <div className="card p-12 text-center text-gray-400">
                            <FiRefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-500" />
                            {window.t('zarezhdane_na_mestata')}
                        </div>
                    ) : performanceData ? (
                        <div className="card p-6">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-xl font-bold text-white">
                                        {performanceData.event?.title || `${window.t('kasa')}`}
                                    </h2>
                                    <div className="flex flex-wrap gap-4 mt-2 text-gray-400 text-sm">
                                        <span>{performanceData.hall?.name}</span>
                                        {selectedSector && sectors.length > 1 && (
                                            <span className="text-primary-400 font-medium">› {getSectorName(selectedSector)}</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-400">
                                        {selectedSector && sectors.length > 1
                                            ? window.t('kliknete_varhu_mestata_za_da_g')
                                            : window.t('kliknete_varhu_mestata_za_da_g')}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm text-gray-400">{window.t('bazova_tsena')}</div>
                                    <div className="text-lg font-bold text-white">€{parseFloat(performanceData.base_price)}</div>
                                </div>
                            </div>

                            {sectors.length > 1 && !selectedSector && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">{window.t('izberete_sektor')}</h3>
                                    <p className="text-gray-400 text-sm mb-4">{window.t('izberete_sektor_za_da_vidite_nalichnite_mesta')}</p>
                                    <SectorMap
                                        sectors={sectors}
                                        seats={seats}
                                        soldSeatIds={soldSeatIds}
                                        lockedSeatIds={lockedSeatIds}
                                        onSectorSelect={handleSectorSelect}
                                    />
                                </div>
                            )}

                            {sectors.length > 1 && selectedSector && (
                                <button
                                    onClick={() => {
                                        // clearCart()
                                        setSelectedSector(null)
                                    }}
                                    className="flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-4 transition-colors"
                                >
                                    <FiChevronLeft className="w-4 h-4" /> {window.t('nazad_kam_sektorite')}
                                </button>
                            )}

                            <div className="bg-gray-900 rounded-xl relative">
                                <div className="pb-8">
                                    <SeatMap
                                        hall={performanceData.hall}
                                        seats={visibleSeats}
                                        soldSeatIds={soldSeatIds}
                                        lockedSeatIds={lockedSeatIds}
                                        selectedSector={selectedSector}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card p-12 text-center border-dashed border-gray-700">
                            <div className="bg-gray-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiCreditCard className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-medium text-white mb-2">{window.t('kasov_modul')}</h3>
                            <p className="text-gray-400 max-w-md mx-auto">
                                {window.t('izberete_sabitie_i_prozhektsiy')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
